import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../components/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiArrowLeft, FiMail, FiGlobe, FiGithub, FiLinkedin,
  FiBriefcase, FiCode, FiBookOpen, FiExternalLink, FiUser,
  FiMapPin, FiCheckCircle
} from 'react-icons/fi';
import { FaGraduationCap } from 'react-icons/fa';

const PublicProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedEmail, setCopiedEmail] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'Users', userId));
        if (docSnap.exists()) setUserData(docSnap.data());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchUserData();
  }, [userId]);

  const copyEmail = () => {
    if (userData?.email) {
      navigator.clipboard.writeText(userData.email);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    }
  };

  const skills = userData?.skills
    ? (Array.isArray(userData.skills) ? userData.skills : userData.skills.split(',').map(s => s.trim()).filter(Boolean))
    : [];

  const c = {
    bg: 'var(--bg-main)',
    card: 'var(--bg-card)',
    surface: 'var(--bg-surface)',
    border: 'var(--border)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)',
    accent: 'var(--accent)',
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: c.bg }}>
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
          style={{ width: 36, height: 36, borderRadius: '50%', border: `3px solid ${c.accent}`, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!userData) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: c.bg, flexDirection: 'column', gap: 16 }}>
        <FiUser size={48} style={{ color: c.muted, opacity: 0.4 }} />
        <p style={{ color: c.text, fontSize: 18, fontWeight: 700 }}>Profile not found</p>
        <button onClick={() => navigate(-1)} style={{ padding: '10px 24px', borderRadius: 12, background: c.accent, border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Go Back</button>
      </div>
    );
  }

  const name = userData.fullName || userData.displayName || 'Student';
  const avatar = userData.photo;
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const SectionCard = ({ icon, title, children }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 20, padding: '28px 28px 24px', marginBottom: 20 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.accent }}>
          {icon}
        </div>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: c.text }}>{title}</h2>
      </div>
      {children}
    </motion.div>
  );

  return (
    <div style={{ minHeight: '100vh', background: c.bg, position: 'relative' }}>
      {/* Decorative glow */}
      <div style={{ position: 'fixed', top: '-10%', right: '-5%', width: 400, height: 400, background: 'rgba(34,197,94,0.06)', filter: 'blur(80px)', borderRadius: '50%', pointerEvents: 'none', zIndex: 0 }} />

      <div style={{ maxWidth: 840, margin: '0 auto', padding: '0 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Back Button */}
        <div style={{ padding: '24px 0 0' }}>
          <button onClick={() => navigate(-1)} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: c.card, border: `1px solid ${c.border}`, borderRadius: 12,
            padding: '8px 16px', color: c.text, fontSize: 13, fontWeight: 700, cursor: 'pointer'
          }}>
            <FiArrowLeft size={15} /> Back
          </button>
        </div>

        {/* ── HERO CARD ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          style={{
            background: c.card, border: `1px solid ${c.border}`,
            borderRadius: 24, overflow: 'hidden', marginTop: 20, marginBottom: 20
          }}
        >
          {/* Cover */}
          <div style={{ height: 110, background: 'linear-gradient(135deg, rgba(34,197,94,0.3) 0%, rgba(16,185,129,0.2) 50%, rgba(34,197,94,0.1) 100%)' }} />

          {/* Profile content */}
          <div style={{ padding: '0 28px 28px', marginTop: -50 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              {/* Avatar */}
              <div style={{
                width: 90, height: 90, borderRadius: 22,
                background: avatar ? 'transparent' : `linear-gradient(135deg, ${c.accent}, #16a34a)`,
                border: `4px solid ${c.bg}`, overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 32, fontWeight: 900, color: '#fff', flexShrink: 0
              }}>
                {avatar ? <img src={avatar} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
              </div>

              {/* Hire / Email buttons */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingTop: 52 }}>
                {userData.availableForWork && (
                  <span style={{ padding: '5px 12px', borderRadius: 100, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: c.accent, fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <FiCheckCircle size={11} /> Available for Work
                  </span>
                )}
                {userData.email && (
                  <button onClick={copyEmail} style={{
                    padding: '8px 18px', borderRadius: 12, background: c.accent, border: 'none',
                    color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
                  }}>
                    <FiMail size={13} /> {copiedEmail ? 'Email Copied!' : 'Copy Email'}
                  </button>
                )}
              </div>
            </div>

            {/* Name, headline, college */}
            <h1 style={{ margin: '14px 0 4px', fontSize: 'clamp(1.3rem, 3vw, 1.9rem)', fontWeight: 900, color: c.text, letterSpacing: '-0.02em' }}>
              {name}
            </h1>
            {userData.headline && (
              <p style={{ margin: '0 0 6px', fontSize: 15, color: c.accent, fontWeight: 600 }}>{userData.headline}</p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13, color: c.muted, marginTop: 6 }}>
              {(userData.college || userData.collegeName) && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <FaGraduationCap size={13} style={{ color: c.accent }} />
                  {userData.college || userData.collegeName}
                  {userData.branch && ` • ${userData.branch.toUpperCase()}`}
                  {userData.semester && ` • ${userData.semester} Sem`}
                </span>
              )}
              {userData.location && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <FiMapPin size={12} style={{ color: c.accent }} /> {userData.location}
                </span>
              )}
            </div>

            {/* Bio */}
            {userData.bio && (
              <p style={{ margin: '14px 0 0', fontSize: 14, color: c.muted, lineHeight: 1.65, maxWidth: 680 }}>
                {userData.bio}
              </p>
            )}

            {/* Social links */}
            <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              {userData.linkedinUrl && (
                <a href={userData.linkedinUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10, background: 'rgba(10,102,194,0.1)', border: '1px solid rgba(10,102,194,0.2)', color: '#0a66c2', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                  <FiLinkedin size={13} /> LinkedIn
                </a>
              )}
              {userData.githubUrl && (
                <a href={userData.githubUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10, background: c.surface, border: `1px solid ${c.border}`, color: c.text, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                  <FiGithub size={13} /> GitHub
                </a>
              )}
              {userData.portfolioUrl && (
                <a href={userData.portfolioUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10, background: c.surface, border: `1px solid ${c.border}`, color: c.text, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                  <FiGlobe size={13} /> Portfolio
                </a>
              )}
              {/* LinkedIn for company too */}
              {userData.linkedin && !userData.linkedinUrl && (
                <a href={userData.linkedin} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10, background: 'rgba(10,102,194,0.1)', border: '1px solid rgba(10,102,194,0.2)', color: '#0a66c2', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                  <FiLinkedin size={13} /> LinkedIn
                </a>
              )}
              {userData.website && (
                <a href={userData.website.startsWith('http') ? userData.website : `https://${userData.website}`} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10, background: c.surface, border: `1px solid ${c.border}`, color: c.text, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                  <FiGlobe size={13} /> Website
                </a>
              )}
            </div>
          </div>
        </motion.div>

        {/* ── SKILLS ── */}
        {skills.length > 0 && (
          <SectionCard icon={<FiCode size={16} />} title="Skills">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {skills.map((skill, i) => (
                <motion.span key={i}
                  whileHover={{ scale: 1.05 }}
                  style={{ padding: '6px 14px', borderRadius: 100, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: c.accent, fontSize: 13, fontWeight: 700 }}>
                  {skill}
                </motion.span>
              ))}
            </div>
          </SectionCard>
        )}

        {/* ── EXPERIENCE ── */}
        {userData.experiences?.length > 0 && (
          <SectionCard icon={<FiBriefcase size={16} />} title="Experience">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {userData.experiences.map((exp, i) => (
                <div key={i} style={{ paddingLeft: 16, borderLeft: `3px solid ${c.accent}` }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: c.text }}>{exp.title}</div>
                  <div style={{ fontSize: 13, color: c.accent, fontWeight: 600, marginTop: 2 }}>{exp.company}</div>
                  {(exp.startDate || exp.endDate) && (
                    <div style={{ fontSize: 12, color: c.muted, marginTop: 3 }}>
                      {exp.startDate} {exp.endDate ? `— ${exp.endDate}` : '— Present'}
                    </div>
                  )}
                  {exp.description && (
                    <p style={{ fontSize: 13, color: c.muted, margin: '8px 0 0', lineHeight: 1.6 }}>{exp.description}</p>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* ── PROJECTS ── */}
        {userData.projects?.length > 0 && (
          <SectionCard icon={<FiCode size={16} />} title="Projects">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              {userData.projects.map((proj, i) => (
                <motion.div key={i} whileHover={{ y: -4 }}
                  style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, padding: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: c.text }}>{proj.title}</div>
                    {proj.link && (
                      <a href={proj.link} target="_blank" rel="noopener noreferrer"
                        style={{ color: c.accent, display: 'flex', alignItems: 'center' }}>
                        <FiExternalLink size={14} />
                      </a>
                    )}
                  </div>
                  {proj.technologies && (
                    <div style={{ fontSize: 11, color: c.accent, fontWeight: 700, marginBottom: 6 }}>{proj.technologies}</div>
                  )}
                  {proj.description && (
                    <p style={{ fontSize: 12, color: c.muted, margin: 0, lineHeight: 1.6 }}>{proj.description}</p>
                  )}
                </motion.div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* ── EDUCATION ── */}
        {(userData.college || userData.education?.length > 0) && (
          <SectionCard icon={<FaGraduationCap size={16} />} title="Education">
            {userData.education?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {userData.education.map((edu, i) => (
                  <div key={i} style={{ paddingLeft: 16, borderLeft: `3px solid var(--accent)` }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: c.text }}>{edu.degree}</div>
                    <div style={{ fontSize: 13, color: c.accent, fontWeight: 600 }}>{edu.institution}</div>
                    {edu.field && <div style={{ fontSize: 12, color: c.muted }}>Field: {edu.field}</div>}
                    {(edu.startDate || edu.endDate) && (
                      <div style={{ fontSize: 12, color: c.muted }}>{edu.startDate} — {edu.endDate || 'Present'}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ paddingLeft: 16, borderLeft: `3px solid ${c.accent}` }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: c.text }}>
                  {userData.college || userData.collegeName}
                </div>
                {userData.branch && (
                  <div style={{ fontSize: 13, color: c.accent, fontWeight: 600, marginTop: 2 }}>
                    B.Tech {userData.branch.toUpperCase()}
                    {userData.semester ? ` • ${userData.semester} Semester` : ''}
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        )}

        {/* ── CONTACT ── (email only, no mobile for privacy) */}
        {userData.email && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(16,185,129,0.1) 100%)', border: `1px solid rgba(34,197,94,0.2)`, borderRadius: 20, padding: 28, textAlign: 'center' }}
          >
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: c.text }}>Let's Connect</h2>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: c.muted, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
              Interested in working together? Reach out via email.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
              <a href={`mailto:${userData.email}`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', borderRadius: 14, background: c.accent, color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 700 }}>
                <FiMail size={15} /> Send Email
              </a>
              <button onClick={copyEmail}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', borderRadius: 14, background: c.card, border: `1px solid ${c.border}`, color: c.text, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                <FiMail size={15} /> {copiedEmail ? 'Copied!' : 'Copy Email'}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PublicProfile;